import React, { useState, useContext, useEffect } from "react";
import TailwindWrapper from "../components/TailwindWrapper/TailwindWrapper";
import EMPStatusTab from "../shared/components/EMPStatusTab/EMPStatusTab";
import StripeOnboardingEmbedded from "./stripe/StripeOnboardingEmbedded";
import GetStarted from "./GetStarted";
import { useStripeOnboarding } from "../shared/hooks/useStripeOnboarding";
import { useStripeContext } from "../shared/context/StripeContext";
import EMPBanner from "../shared/components/EMPBanner/EMPBanner";
import { usePartsOnlyRole } from "../shared/hooks/usePartsOnlyRole";
import { UserContext } from "../shared/context/userContext";
import { useTranslation } from "react-i18next";
import ConfigurePricing from "./pricing/ConfigurePricing";

/**
 * Main onboarding page with responsive layout:
 * - Mobile/Tablet: Status tabs full width above content (stacked vertically)
 * - Desktop: Two-column layout (tabs on left 30%, content on right 70%)
 */
const OnboardingPage = () => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState("get-started");

  // Get Stripe status for tab states
  const { isLoading: stripeLoading, handleInitializeStripe } =
    useStripeOnboarding();

  // Get Stripe context for status display
  const { getStripeStatusDisplay } = useStripeContext();
  const stripeStatusDisplay = getStripeStatusDisplay();

  // Get user context and parts-only role for EMPBanner
  const { user } = useContext(UserContext);
  const isPartsOnly = usePartsOnlyRole(user);

  // Initialize Stripe data when the page loads
  useEffect(() => {
    handleInitializeStripe();
  }, [handleInitializeStripe]);

  const handleTabClick = (tabId) => {
    setSelectedTab(tabId);
  };

  const renderSelectedComponent = () => {
    switch (selectedTab) {
      case "get-started":
        return <GetStarted user={user} />;
      case "stripe":
        return <StripeOnboardingEmbedded user={user} />;
      case "configure-pricing":
        return <ConfigurePricing user={user} />;
      default:
        return <GetStarted user={user} />;
    }
  };

  return (
    <TailwindWrapper>
      <div
        className="tw-min-h-screen tw-px-4 tw-py-4 1440:tw-px-14"
        style={{ borderTop: "1px solid #e5e5e5" }}
      >
        <div className={!isPartsOnly ? "tw-mt-10" : ""}>
          {isPartsOnly && (
            <div className="tw-mb-10">
              <EMPBanner
                title={t("NVCOnboarding.stripe.banner.title")}
                content={t("NVCOnboarding.stripe.banner.description")}
                severity="info"
              />
            </div>
          )}

          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-4 tw-h-full">
            <div className="tw-col-span-1 lg:tw-col-span-4">
              <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 lg:tw-grid-cols-1 tw-gap-2 lg:tw-gap-0 lg:tw-space-y-2 lg:tw-mr-6">
                <EMPStatusTab
                  id="get-started"
                  title={t("NVCOnboarding.tabs.0.title")}
                  onClick={handleTabClick}
                  isActive={selectedTab === "get-started"}
                  disabled={stripeLoading}
                />

                <EMPStatusTab
                  id="stripe"
                  title={t("NVCOnboarding.tabs.1.title")}
                  description={t("NVCOnboarding.tabs.1.description")}
                  onClick={handleTabClick}
                  isActive={selectedTab === "stripe"}
                  disabled={stripeLoading}
                  hasStatus
                  customChipLabel={stripeStatusDisplay.chipLabel}
                  customChipLabelColor={stripeStatusDisplay.chipLabelColor}
                />
                <EMPStatusTab
                  id="configure-pricing"
                  title={t("NVCOnboarding.tabs.2.title")}
                  description={t("NVCOnboarding.tabs.2.description")}
                  onClick={handleTabClick}
                  isActive={selectedTab === "configure-pricing"}
                  disabled={stripeLoading}
                />
              </div>
            </div>

            <div className="tw-col-span-1 lg:tw-col-span-8">
              <div>{renderSelectedComponent()}</div>
            </div>
          </div>
        </div>
      </div>
    </TailwindWrapper>
  );
};

export default OnboardingPage;
